// Firestore Service Layer
// Handles all database operations for Categories, Subcategories, and ContactInfo

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Collection names
const COLLECTIONS = {
  CATEGORIES: 'categories',
  SUBCATEGORIES: 'subcategories',
  CONTACT_INFO: 'contactInfo',
  ADMIN_USERS: 'adminUsers'
};

// ==================== CATEGORIES ====================

/**
 * Get all categories from Firestore
 */
export const getCategories = async () => {
  try {
    const categoriesRef = collection(db, COLLECTIONS.CATEGORIES);
    const q = query(categoriesRef, orderBy('created_at', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const categories = [];
    querySnapshot.forEach((doc) => {
      categories.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return categories;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

/**
 * Add a new category to Firestore
 */
export const addCategory = async (categoryData) => {
  try {
    console.log('🔥 Firestore: Adding category to database...', categoryData);
    const categoriesRef = collection(db, COLLECTIONS.CATEGORIES);
    
    // Remove id from data (Firestore will generate it)
    const { id, ...dataWithoutId } = categoryData;
    
    const docRef = await addDoc(categoriesRef, {
      ...dataWithoutId,
      name: categoryData.name, // Ensure name is included
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
    
    const result = { id: docRef.id, ...categoryData };
    console.log('✅ Firestore: Category added successfully!', result);
    return result;
  } catch (error) {
    console.error('❌ Firestore Error adding category:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    throw error;
  }
};

/**
 * Update a category in Firestore
 */
export const updateCategory = async (categoryId, categoryData) => {
  try {
    const categoryRef = doc(db, COLLECTIONS.CATEGORIES, categoryId);
    await updateDoc(categoryRef, {
      ...categoryData,
      updated_at: new Date().toISOString()
    });
    
    return { id: categoryId, ...categoryData };
  } catch (error) {
    console.error('Error updating category:', error);
    throw error;
  }
};

/**
 * Delete a category from Firestore
 */
export const deleteCategory = async (categoryId) => {
  try {
    const categoryRef = doc(db, COLLECTIONS.CATEGORIES, categoryId);
    await deleteDoc(categoryRef);
    
    // Also delete all related subcategories
    const subcategories = await getSubcategories();
    const relatedSubs = subcategories.filter(sub => sub.category_id === categoryId);
    
    for (const sub of relatedSubs) {
      await deleteSubcategory(sub.id);
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
};

// ==================== SUBCATEGORIES ====================

/**
 * Get all subcategories from Firestore
 */
export const getSubcategories = async () => {
  try {
    const subcategoriesRef = collection(db, COLLECTIONS.SUBCATEGORIES);
    const q = query(subcategoriesRef, orderBy('created_at', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const subcategories = [];
    querySnapshot.forEach((doc) => {
      subcategories.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return subcategories;
  } catch (error) {
    console.error('Error fetching subcategories:', error);
    throw error;
  }
};

/**
 * Add a new subcategory to Firestore
 */
export const addSubcategory = async (subcategoryData) => {
  try {
    const subcategoriesRef = collection(db, COLLECTIONS.SUBCATEGORIES);
    
    // Convert category_id to string if it's a number (for Firestore compatibility)
    const data = {
      ...subcategoryData,
      category_id: String(subcategoryData.category_id),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    const docRef = await addDoc(subcategoriesRef, data);
    
    return { id: docRef.id, ...data };
  } catch (error) {
    console.error('Error adding subcategory:', error);
    throw error;
  }
};

/**
 * Update a subcategory in Firestore
 */
export const updateSubcategory = async (subcategoryId, subcategoryData) => {
  try {
    const subcategoryRef = doc(db, COLLECTIONS.SUBCATEGORIES, subcategoryId);
    
    const data = {
      ...subcategoryData,
      category_id: String(subcategoryData.category_id),
      updated_at: new Date().toISOString()
    };
    
    await updateDoc(subcategoryRef, data);
    
    return { id: subcategoryId, ...data };
  } catch (error) {
    console.error('Error updating subcategory:', error);
    throw error;
  }
};

/**
 * Delete a subcategory from Firestore
 */
export const deleteSubcategory = async (subcategoryId) => {
  try {
    const subcategoryRef = doc(db, COLLECTIONS.SUBCATEGORIES, subcategoryId);
    await deleteDoc(subcategoryRef);
    return true;
  } catch (error) {
    console.error('Error deleting subcategory:', error);
    throw error;
  }
};

// ==================== CONTACT INFO ====================

/**
 * Get contact info from Firestore
 */
export const getContactInfo = async () => {
  try {
    const contactInfoRef = doc(db, COLLECTIONS.CONTACT_INFO, 'main');
    const docSnap = await getDoc(contactInfoRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      // Return default if not exists
      return null;
    }
  } catch (error) {
    console.error('Error fetching contact info:', error);
    throw error;
  }
};

/**
 * Update contact info in Firestore
 */
export const updateContactInfo = async (contactData) => {
  try {
    const contactInfoRef = doc(db, COLLECTIONS.CONTACT_INFO, 'main');
    await setDoc(contactInfoRef, {
      ...contactData,
      updated_at: new Date().toISOString()
    }, { merge: true });
    
    return contactData;
  } catch (error) {
    console.error('Error updating contact info:', error);
    throw error;
  }
};

// ==================== REAL-TIME LISTENERS ====================

/**
 * Subscribe to categories changes (real-time updates)
 */
export const subscribeToCategories = (callback) => {
  const categoriesRef = collection(db, COLLECTIONS.CATEGORIES);
  const q = query(categoriesRef, orderBy('created_at', 'desc'));
  
  return onSnapshot(q, (querySnapshot) => {
    const categories = [];
    querySnapshot.forEach((doc) => {
      categories.push({
        id: doc.id,
        ...doc.data()
      });
    });
    callback(categories);
  }, (error) => {
    console.error('Error in categories subscription:', error);
    callback([]);
  });
};

/**
 * Subscribe to subcategories changes (real-time updates)
 */
export const subscribeToSubcategories = (callback) => {
  const subcategoriesRef = collection(db, COLLECTIONS.SUBCATEGORIES);
  const q = query(subcategoriesRef, orderBy('created_at', 'desc'));
  
  return onSnapshot(q, (querySnapshot) => {
    const subcategories = [];
    querySnapshot.forEach((doc) => {
      subcategories.push({
        id: doc.id,
        ...doc.data()
      });
    });
    callback(subcategories);
  }, (error) => {
    console.error('Error in subcategories subscription:', error);
    callback([]);
  });
};

/**
 * Subscribe to contact info changes (real-time updates)
 */
export const subscribeToContactInfo = (callback) => {
  const contactInfoRef = doc(db, COLLECTIONS.CONTACT_INFO, 'main');
  
  return onSnapshot(contactInfoRef, (docSnap) => {
    if (docSnap.exists()) {
      callback(docSnap.data());
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('Error in contact info subscription:', error);
    callback(null);
  });
};

